/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderNode from "App/render/webgl/nodes/render_node";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import RenderObject from "App/geometry/objects/object";
import Line from "App/geometry/objects/line";
import {LinkedShapesList} from "App/render/webgl/utils/linked_shapes_list";
import ArrayBufferHandler from "App/render/webgl/utils/array_buffer_handler";
import {logInfo} from "App/debug";
import Batch from "App/render/webgl/renderables/batches/batch";
import TextureNode from "App/render/webgl/nodes/texture_node";
import ModelStatistics from "App/model_statistics";
import Pass from "App/render/webgl/passes/pass";
import Shape from "App/geometry/objects/shape";
import Factory from "App/render/webgl/nodes/factory";
import PrimitiveId from "App/render/webgl/passes/primitive_id";
import {ShapeBatchNodeTypeName} from "App/render/webgl/nodes/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class ShapeBatchNode extends RenderNode {

    public static TypeName: string = ShapeBatchNodeTypeName ;
    private static registeredGeometryObjects: {} = {};

    public   placements: LinkedShapesList;
    private verticesCount: number;
    private indicesCount: number;
    private renderable: Batch;
    private _visibilityCache: { visible: boolean; disabled: boolean; updated: boolean };
    private _reCreateGLBuffers: boolean;
    private _needUpdate: boolean;
    private _minVerticesCount: number;
    private _minIndicesCount: number;
    private _usedVerticesCount: number;
    private _maxVerticesCount: number;
    private _maxIndicesCount: number;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        super(aSceneAPI, aRenderGraph, aParentNode, aShape);

        this._typeName = ShapeBatchNode.TypeName;

        this.verticesCount = 0;
        this.indicesCount = 0;

        let type = aShape.getType();
        if (type === "LINE") {
            if ((aShape as Line).getRenderAsOutline()) {
                type = "OUTLINE";
            }
        }

        this.renderable = ShapeBatchNode.createRenderableBatch(type, this._sceneAPI, this._renderGraph, this);

        this._visibilityCache = {
            visible: false,
            updated: true,
            disabled: false
        };

        this._reCreateGLBuffers = false;
        this._needUpdate = false;

        this.placements = new LinkedShapesList();

        this._minVerticesCount = 0;
        this._minIndicesCount = 0;

        this._usedVerticesCount = 0;

        this._maxVerticesCount = ArrayBufferHandler.maxVerticesCount;
        this._maxIndicesCount = -1;
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aParentNode: RenderNode, aShape: RenderObject) {
        return new ShapeBatchNode(aSceneAPI, aRenderGraph, aParentNode, aShape);
    }

    public static registerGeometryObject(aClassTypeName: string, aRenderableBatchClass: any) {
        ShapeBatchNode.registeredGeometryObjects[aClassTypeName] = aRenderableBatchClass;
    }

    public static createRenderableBatch(aClassTypeName: string, aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph, aShapeBatchNode: ShapeBatchNode): Batch {
        let registeredClass = ShapeBatchNode.registeredGeometryObjects[aClassTypeName];
        if (registeredClass === undefined) {
            logInfo("Error: cannot determine batch renderable for geometry object of type " +
                    aClassTypeName + ". Please register the type using ShapeBatchNode.registerGeometryObject method first.");
        }

        let instance = registeredClass.create(aSceneAPI, aRenderGraph, aShapeBatchNode);
        return instance;
    }

    public findTextureNode(): TextureNode {
        let parent = this._parent;
        while (parent) {
            if (parent.getTypeName() === TextureNode.TypeName) {
                return parent as TextureNode;
            }
            parent = parent.getParent();
        }

        return null;
    }

    public setSizePolicy(aMinVerticesCount: number, aMaxVerticesCount: number, aMinIndicesCount: number, aMaxIndicesCount: number) {
        this._minVerticesCount = aMinVerticesCount;
        this._minIndicesCount = aMinIndicesCount;

        if (aMaxVerticesCount) {
            this._maxVerticesCount = aMaxVerticesCount;
        }

        if (this._maxVerticesCount < this._minVerticesCount) {
            this._maxVerticesCount = this._minVerticesCount;
        }

        if (aMaxIndicesCount) {
            this._maxIndicesCount = aMaxIndicesCount;
        }

        if (this._maxIndicesCount < this._minIndicesCount) {
            this._maxIndicesCount = this._minIndicesCount;
        }

        this._renderGraph.scheduleReserveGLBuffersSize();
    }

    public canAcceptShape(aShape: RenderObject): boolean {
        // only one svshape per batch is allowed (i.e. the batching has no effect)
        if (!this.isEmpty() && (aShape.getType() === "SVSIDESHAPE" || aShape.getType() === "SVCAPSHAPE")) {
            return false;
        }

        // do not consider max indices count as it does not matter
        return (this._usedVerticesCount + aShape.getVerticesCount()) < this._maxVerticesCount;
    }

    /***
     * Function is called when the geometry is ready (downloaded and processed)
     * @param aShape a geometry object which received and process (created a mesh) geometry data
     */
    public notifyMeshCreated(aShape: RenderObject) {
        // the aShape should have its place in the buffer reserved so we just need to update it
        aShape.setNeedUpdateAll();
    }

    public setNeedUpdate() {
        this._needUpdate = true;
        this._renderGraph.scheduleShapesNeedUpdate();
    }

    public scheduleReCreateGLBuffers() {
        this._reCreateGLBuffers = true;
        this._renderGraph.scheduleReCreateGLBuffers();
    }

    /**
     * Here the addShape function finishes with estimating number of vertices and indices.
     * The geometry is scheduled to be added to the renderable in the notifyMeshCreated callback.
     *
     * @param {object} aShape
     */
    public addShape(aShape: RenderObject) {
        this._usedVerticesCount += aShape.getVerticesCount();

        this.placements.push(aShape);

        this._renderGraph.scheduleReserveGLBuffersSize();
    }

    /**
     * Here we will only schedule geometry releasing in case the batch is empty, the real work
     * (deleting this reference adnd the gl resources releasing) is done in the gl release pass
     *
     * @param {object} aShape
     */
    public removeShape(aShape: RenderObject): boolean {
        let item = aShape.getPlacement();
        if (!item) {
            return false;
        }

        this.placements.remove(item);

        aShape.removed();

        if (this.isEmpty()) {
            // notify parent about that we need to remove this batch node
            this._parent.childRemoved(this);
        }

        return true;
    }

    public removed() {
        this._renderGraph.scheduleReleaseGLObject(this.renderable);
    }

    public isEmpty(): boolean {
        return this.placements.isEmpty();
    }

    public reserveBuffers() {
        // this does not distinguish between shapes with already received geometry and without it (only estimated vertices count is known here)
        this._updateVerticesAndIndicesCount();

        if (!this.renderable.checkVerticesFitToBuffers(this.verticesCount)) {
            this._reCreateGLBuffers = true;
            this._renderGraph.scheduleReCreateGLBuffers();
        }
    }

    public reCreateBuffers() {
        if (this._reCreateGLBuffers) {
            this.renderable.reCreate(Math.max(this._minVerticesCount, this.verticesCount), Math.max(this._minIndicesCount, this.indicesCount));

            // place all shapes which has mesh already available to the buffers again
            this.placements.resetPlacement();

            this._reCreateGLBuffers = false;

            this._needUpdate = true;
            this._renderGraph.scheduleShapesNeedUpdate();

            let shapes = this.placements.getObjectsArray();
            for (let shape of shapes) {
                shape.setNeedUpdateAll();
            }
        }
    }

    public updateBuffers() {
        if (this._needUpdate) {

            this._updateVerticesAndIndicesCount();

            if (!this.renderable.checkVerticesFitToBuffers(this.verticesCount)) {
                this._reCreateGLBuffers = true;
                this.reCreateBuffers();
            }

            this._visibilityCache.updated = true;

            this.renderable.update();

            this._needUpdate = false;
        }

        this.renderableReady = true;
    }

    public countShapesAndUniqueTextures(aStatistics: ModelStatistics) {
        let shapes = this.placements.getObjectsArray();
        for (let shape of shapes) {
            aStatistics.addShape(shape);
        }

        aStatistics.addShapeBatchNode();
    }

    public render(aRenderPass: Pass): boolean {
        if (!this.renderableReady) {
            return false;
        }

        if (this.verticesCount === 0) {
            return false;
        }

        if (!this._isVisible()) {
            return false;
        }

        if (aRenderPass.getClassTypeName() === PrimitiveId.TypeName) {
            let geometryObject: Shape = aRenderPass.state.getRenderedSingleGeometryObject() as Shape;
            if (!geometryObject) {
                return false;
            }

            let renderGraphObject = geometryObject.getRenderGraphObject();
            if (renderGraphObject && renderGraphObject.getUID() === this.getUID()) {
                let placement = geometryObject.getPlacement();
                if (placement) {
                    let nofElements = 0;
                    if (geometryObject.getGeometry().nonIndexedPositionsDesc) {
                        nofElements = geometryObject.getGeometry().nonIndexedPositionsDesc.nofElements;
                    } else {
                        nofElements = geometryObject.getGeometry().positionsDesc.nofElements;
                    }

                    this.renderable.renderPrimitiveId(aRenderPass, placement, nofElements);

                    return true;
                }
            }

            return false;
        } else {
            this.renderable.render(aRenderPass);
        }

        return true;
    }

    public release() {
        if (this.renderable) {
            this.renderable.release();
            this.renderableReady = false;
        }
    }

    public invalidateVisibilityCache() {
        this._visibilityCache.updated = true;
    }

    private _updateVerticesAndIndicesCount() {
        this.verticesCount = 0;
        this.indicesCount = 0;
        let shapes = this.placements.getObjectsArray();
        for (let shape of shapes) {
            this.verticesCount += shape.getVerticesCount();
            this.indicesCount += shape.getIndicesCount();
        }

        this._usedVerticesCount = this.verticesCount;
    }

    private _isVisible(): boolean {
        if (this._visibilityCache.updated) {
            this._visibilityCache.visible = false;
            this._visibilityCache.updated = false;

            let shapes = this.placements.getObjectsArray();
            for (let i = 0; i < shapes.length && !this._visibilityCache.visible; i++) {
                this._visibilityCache.visible = this._visibilityCache.visible || shapes[i].getVisibility();
            }
        }

        return this._visibilityCache.visible;
    }
}

Factory.register(ShapeBatchNode);
